/**
 * Shared utilities for agent contact/identity signal processing.
 * Used by both `agent-customer-quick-create` and `agent-customer-update-offer` components.
 */

import { AgentExtractedIdentitySignals } from './agent-data';

/**
 * Extracts the last 9 digits of a phone number, stripping all non-digits.
 * Used for phone deduplication — differently-formatted but numerically identical
 * numbers still match (e.g. "38(0XX) XXX-XX-XX" and "0XXXXXXXXX").
 *
 * @param v - Phone string or null
 * @returns Last 9 digits, or empty string if fewer than 9 digits or input is null
 */
export function phoneDigits(v: string | null): string {
  return (v ?? '').replace(/\D/g, '').slice(-9);
}

/**
 * Splits extracted identity signals into a parsed first/last name pair.
 * Follows the rule: `contactLastName` is preferred as the surname;
 * `contactFullName` provides the given name only if it contains a different token.
 *
 * @param s - Extracted signals from the email signature block
 * @returns Object with `fname` and `lname` properties (both may be null)
 */
export function splitContactName(s: AgentExtractedIdentitySignals | null): { fname: string | null; lname: string | null } {
  if (s?.contactLastName) {
    const parts = (s.contactFullName ?? '').trim().split(/\s+/).filter(p => p);
    const fname = parts.find(p => p !== s.contactLastName) ?? null;
    return { fname, lname: s.contactLastName };
  }
  if (s?.contactFullName) {
    const parts = s.contactFullName.trim().split(/\s+/).filter(p => p);
    return { fname: parts[1] ?? null, lname: parts[0] ?? null };
  }
  return { fname: null, lname: null };
}

/** Ukrainian/Russian legal-form abbreviations that precede a quoted company
 *  name in a typical email signature (e.g. `ТОВ "АБР Електрик"`). `ФОП` is
 *  included for completeness even though a sole proprietor's "name" is
 *  usually just a person's full name, not a quoted brand name -- the regex
 *  below simply won't match in that case and `deriveShortNameCandidate()`
 *  returns null, which is the correct outcome. */
const LEGAL_FORM_PREFIXES = ['ПрАТ', 'ТДВ', 'ТОВ', 'ПАТ', 'АТ', 'ПП', 'ФОП', 'ДП', 'КП'];
const OPEN_QUOTES = '"«„';
const CLOSE_QUOTES = '"»”';
const SHORT_NAME_PATTERN = new RegExp(
  `^(?:${LEGAL_FORM_PREFIXES.join('|')})\\.?\\s+[${OPEN_QUOTES}]([^${CLOSE_QUOTES}]+)[${CLOSE_QUOTES}]\\s*$`,
  'iu',
);

/**
 * Derives a short-name candidate from a full company name extracted from an
 * email signature, e.g. `ТОВ "АБР Електрик"` -> `АБР Електрик`. Purely
 * mechanical string parsing -- deliberately not an LLM call, same reasoning
 * as `parseRegisterAddress()` (`register-data-parsing.ts`): stripping a
 * known legal-form prefix and unquoting the brand name is a fixed,
 * deterministic transformation, not a judgment call worth spending a model
 * call on.
 *
 * Returns null when the full name doesn't match the "PREFIX + quoted name"
 * shape (no legal-form prefix recognized, or the name isn't quoted at all,
 * as is typical for a FOP signature that's just a person's name) -- the
 * caller falls back to the full name itself in that case, never to an
 * empty string.
 *
 * @param fullName - Extracted company full name, or null
 * @returns The quoted brand-name substring, or null if the shape doesn't match
 */
export function deriveShortNameCandidate(fullName: string | null): string | null {
  if (!fullName) { return null; }
  const match = fullName.trim().match(SHORT_NAME_PATTERN);
  if (!match) { return null; }
  const candidate = match[1].trim();
  return candidate || null;
}
